<template>
  <div class="page-block">
    <template v-if="entityType === 'LogPage'">
      <log-page />
    </template>
    <template v-else-if="entityType === 'ElasticLogs'">
      <elastic-log />
    </template>

    <a-spin v-else :spinning="!data || !config || loadingQueries > 0">
      <div class="spin-wrap">
        <template v-if="fieldsMeta && config">
          <a-row type="flex">
            <a-col flex="1">
              <page-title
                :title="title"
                :showSidebarButton="showSidebarButton"
                :exportName="exportName"
              />
            </a-col>

            <a-col>
              <the-filter
                :pageConfig="config"
                :formConfig="formConfig"
                :filters="filterParams"
                :configData="cachedConfig"
                :configLayout="CONFIG_LAYOUT"
                :entityFields="fieldsMeta"
                :canCreateModel="canCreateModel"
                @setFilterParams="setFilterParams"
                @create-entity="entityClick"
                @configUpdated="saveStoraged"
              />
            </a-col>
            <a-col>
              <btn-export
                v-if="data && !markup_page"
                :config="appConfig"
                :columns="prepareColumnsForDownload(data)"
                :data="prepareDataForDownload(data)"
                :filename="`${entityType}(${page})`"
                :btnText="appConfig.data.customprops.downloadReportButton.text"
                :btns="[
                  { label: 'Текущая страница XLSX', format: 'xlsx' },
                ]"
              />
            </a-col>
          </a-row>

          <a-row  type="flex">
            <a-col v-if="filter_type_report" flex="1">
              <input-enum
                v-model="selected_type_report"
                class="filter"
                :options="Object.keys(type_reports)"
                :allowClear="false"
                />
            </a-col>
          </a-row>

          <div class="page-block__component">
            <component
              :is="rendererComponent"
              v-if="data"
              :entityType="entityType"
              :entityFields="fieldsMeta"
              :defaultSort="sortParams"
              :data="data"
              :config="config"
              :formConfig="formConfig"
              :canReadModel="canReadModel"
              :canUpdateModel="canUpdateModel"
              @updateEntity="updateEntity"
              @setSortParams="setSortParams"
              @entityClick="entityClick"
              @updateRows="updateRows"
              @configUpdated="saveStoraged"
            />

            <component-pagination
              v-if="data && config"
              :page="page"
              :size="config.pageSize"
              :total="data.totalCount"
              @changePage="page = $event"
              @changeSize="changeSize"
            />
          </div>
        </template>
      </div>
    </a-spin>

    <page-block-config-modal
      v-if="fieldsMeta"
      v-model="isEditingConfig"
      :entityType="entityType"
      :configData="cachedConfig"
      :configLayout="CONFIG_LAYOUT"
      :entityFields="fieldsMeta"
      @configUpdated="saveConfig"
    />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import store from '@/store';
import {
  bus,
  preparePayload,
  storage,
  prepareFormConfig,
  createDefaultFormConfig,
} from '@/helpers';
import CONFIG_QUERY from '@/queries/config';
import META_QUERY from '@/queries/meta';
import TABLE_QUERY from '@/queries/table';
import UPDATE_MUTATION from '@/queries/mutations/update';
import CREATE_MUTATION from '@/queries/mutations/create';
import TheFilter from '@/pages/table/filter/TheFilter.vue';
import TheGantt from '@/pages/table/gantt/TheGantt.vue';
import TheKanban from '@/pages/table/kanban/TheKanban.vue';
import TheTable from '@/pages/table/table/TheTable.vue';
import TheTiles from '@/pages/table/tiles/TheTiles.vue';
import TheCalendar from '@/pages/table/calendar/TheCalendar.vue';
import {CONFIG_LAYOUT, prepareConfig} from '@/pages/table/config/configFormLayout.js';
import PageBlockConfigModal from './PageBlockConfigModal.vue';
import PageTitle from '@/components/page/PageTitle';
import LogPage from '@/components/page/LogPage.vue';
import InputEnum from '@/components/base/InputEnum.vue';
import ElasticLog from "@/components/page/ElasticLogs";

export default {
  name: 'PageBlock',

  components: {
    ElasticLog,
    PageTitle,
    ComponentPagination: () => import('@/components/base/ComponentPagination.vue'),
    BtnExport: () => import('@/components/base/BtnExport.vue'),
    TheFilter,
    InputEnum,
    TheGantt,
    TheKanban,
    TheTable,
    TheInlineTable: TheTable,
    TheTiles,
    TheCalendar,
    PageBlockConfigModal,
    LogPage,
  },

  props: {
    entityType: {
      type: String,
      required: true,
    },
    showSidebarButton: {
      type: Boolean,
      required: true,
    },
    blockTitle: {
      type: String,
      required: true,
    },
    appConfig: {
      type: Object,
      default: null,
    },
    blockConfig: {
      type: Object,
      default: null,
    },
  },

  apollo: {
    meta: {
      ...META_QUERY,
      variables() {
        return {
          type: this.entityType,
        };
      },
      error(error, vm, key, type, {variables}) {
        this.emitError(`Ошибка получения meta-данных. Type: ${variables.type}. ${error.message};`);
      },
    },
    storedConfig: {
      ...CONFIG_QUERY,
      fetchPolicy: 'network-only',
      variables() {
        return {
          name: `pageblock_${this.entityType}`,
        };
      },
      skip() {
        return !this.fieldsMeta;
      },
      update({table}) {
        if (table.totalCount > 0) {
          const configData = table.documents[0].data;
          return {
            ...configData,
            config: JSON.parse(configData.config),
          };
        }

        return null;
      },
      error(error, vm, key, type, {variables}) {
        this.emitError(
          `Ошибка получения конфига таблицы. Name: ${variables.name}. ${error.message};`,
        );
      },
    },
    formConfig: {
      ...CONFIG_QUERY,
      variables() {
        return {
          name: `form_${this.entityType}`,
        };
      },
      error(error, vm, key, type, {variables}) {
        this.emitError(
          `Ошибка получения конфига формы. Name: ${variables.name}. ${error.message};`,
        );
      },
    },
    data: {
      ...TABLE_QUERY,
      loadingKey: 'loadingQueries',
      debounce: 250,
      variables() {
        return {
          type: this.entityType,
          page: this.page - 1,
          pageSize: this.config.pageSize,
          filters: this.tableFilters,
          orders: this.sortParams,
        };
      },
      update({table}) {
        let data = null;
        if (!table) {
          data = {
            totalCount: 0,
            totalPages: 0,
            hasMore: false,
            rows: [],
          };
        } else {
          data = {
            totalCount: table.totalCount,
            totalPages: Math.ceil(table.totalCount / this.config.pageSize),
            hasMore: table.hasMore,
            rows: table.documents,
          };
        }

        return data;
      },
      skip() {
        return !this.componentActive || !this.fieldsMeta || !this.cachedConfig;
      },
      error(error, vm, key, type, {variables}) {
        this.emitError(
          `Ошибка получения данных таблицы. Переменные: ${JSON.stringify(variables, ' ', 5)}; ${
            error.message
          }`,
        );
      },
    },
  },

  data() {
    return {
      loadingQueries: 0,
      CONFIG_LAYOUT,
      page: Number(this.$route.query.page) || 1,
      filterParams: [],
      sortParams: [],
      componentActive: true,
      cachedConfig: null,
      isEditingConfig: false,
      queryPageSize: Number(this.$route.query.pageSize),
      selected_type_report: 'Годовая обработка',
      type_reports: {'Годовая обработка':'annual_sample', 'Регулярная обработка': 'regular_sample'}
    };
  },

  computed: {
    desktop() {
      return store.state.isDesktop;
    },
    exportName(){
      return `${this.entityType}(${this.page})`
    },
    user() {
      return store.state.user;
    },
    config() {
      return this.cachedConfig?.config;
    },
    filter_type_report() {
      return this.blockConfig?.filter_type_report || false;
    },
    markup_page () {
      return this.blockConfig?.markup_page || false;
    },
    title() {
      if (this.blockTitle === undefined) return '';
      if (this.blockTitle) return this.blockTitle;
      if (this.formConfig === undefined) return '';
      return this.formConfig?.form.locale[store.state.lang].entity || this.entityType;
    },
    configData() {
      if (this.storedConfig === undefined) return null;
      const storedConfig = this.storedConfig || this.createDefaultBaseConfig();
      const storaged = storage.get('viewConfigData') || {};
      const storagedByType = storaged[this.$vnode.key] || {};
      storedConfig.config = {
        ...storedConfig.config,
        ...storagedByType,
      };
      storedConfig.config.pageSize = this.queryPageSize || storedConfig.config.pageSize;
      return storedConfig;
    },
    rendererComponent() {
      return `the-${this.config.displayType.value}`;
    },
    fieldsMeta() {
      if (this.meta && this.formConfig !== undefined) {
        let config = this.formConfig
          ? prepareFormConfig(
            this.formConfig,
            this.meta.components[this.entityType].fields,
            store.state.lang,
          )
          : createDefaultFormConfig(
            this.meta.components[this.entityType].fields,
            this.entityType,
            store.state.lang,
          );
        config = config.form.tabs.reduce((fieldsConfDict, tab) => {
          return tab.columns.flat().reduce((flat, field) => {
            fieldsConfDict[field.field] = field;
            fieldsConfDict[field.field].label =
              this.formConfig?.form.locale[store.state.lang].fields[field.field] || field.field;
            return fieldsConfDict;
          }, fieldsConfDict);
        }, {});

        return this.meta.components[this.entityType].fields
          .filter(el => !config[el.name]?.hidden)
          .filter(el => !el.name.startsWith('cms'))
          .map((fieldMeta) => {
          if (fieldMeta.name === 'version') {
            fieldMeta.renderer = "date-time"
          }
          return {
            ...fieldMeta,
            ...config[fieldMeta.name]
          }
        });
      }

      return null;
    },
    isTable() {
      return this.config.displayType.value === 'table';
    },
    tableFilters() {
      return this.filterParams.map((param) => ({
        ...param,
        value: param.value?.value || param.value,
      }));
    },
    operations() {
      return this.meta?.operations || {};
    },
    canCreateModel() {
      return this.markup_page ? false : this.operations.CREATE;
    },
    canReadModel() {
      return this.operations.READ;
    },
    canUpdateModel() {
      return this.operations.UPDATE;
    },
  },

  watch: {
    async configData(configData) {
      if (configData) {
        configData = {
          ...configData,
          config: await prepareConfig(configData.config, this.fieldsMeta, CONFIG_LAYOUT),
        };
      }
      this.cachedConfig = configData;
      this.sortParams = this.getInitialSortParams();
      this.filterParams = this.getInitialFilterParams();
    },
    page() {
      this.updatePageQueryParams();
    },
    selected_type_report() {
      this.setFilterParams(this.filterParams, false)
    },
  },

  activated() {
    this.componentActive = true;
  },

  deactivated() {
    this.componentActive = false;
  },

  created() {
    bus.$on('refreshPageData', this.refreshPageData);
  },

  destroyed() {
    bus.$off('refreshPageData');
  },

  methods: {
    changeSize(size) {
      this.page = 1;
      this.queryPageSize = size;
    },
    routeQueryUpdate(queryParamsToAdd, paramsFilter = () => true) {
      const queryParamsToKeep = Object.entries(this.$route.query).filter(paramsFilter);
      this.$router
        .replace({
          query: {
            ...Object.fromEntries(queryParamsToKeep),
            ...queryParamsToAdd,
          },
        })
        .catch(() => {
        });
    },
    updatePageQueryParams() {
      this.routeQueryUpdate(
        {
          page: this.page,
          pageSize: this.cachedConfig.config.pageSize,
        },
        ([key]) => !['page', 'pageSize'].includes(key),
      );
    },
    refreshPageData() {
      this.$apollo.queries.data.refresh();
    },
    createDefaultBaseConfig() {
      return {
        id: '',
        title: `pageblock_${this.entityType}`,
        config: {
          title: this.entityType,
        },
      };
    },
    startConfigEdit() {
      this.isEditingConfig = true;
    },
    getInitialSortParams() {
      const queryParams = this.isTable && this.$route.query;
      let sortParams = (this.isTable && storage.get(`tableSorting_${this.$vnode.key}`)) || [];
      if (queryParams.sort) {
        sortParams = [
          {
            field: queryParams.sort,
            direction: (queryParams.sortDir || '').toUpperCase(),
          },
        ];
      }

      return sortParams;
    },
    getInitialFilterParams() {
      const queryFilterParams = Object.entries(this.$route.query).filter(([key]) =>
        key.startsWith('f_'),
      );
      let filterParams = storage.get(`tableFiltering_${this.$vnode.key}`) || [];

      if (queryFilterParams.length) {
        filterParams = queryFilterParams.map(([key, value]) => {
          value = value.split(/\./);
          let operator = value.shift();

          if (operator === 'o') {
            operator = value.shift();
            value = {
              value: value.shift(),
              title: value.join('.'),
            };
          } else {
            value = value.join('.');
          }

          return {
            field: key.split('f_')[1],
            operator,
            value,
          };
        });
      }
      if (this.filter_type_report) {
        filterParams.push({
          field: "type_report",
          operator: "EQUALS",
          value: this.type_reports[this.selected_type_report]
        })
      }
      return filterParams;
    },
    setSortParams(params) {
      storage.set(`tableSorting_${this.$vnode.key}`, params);
      this.sortParams = params;
      const queryParamsToAdd = {
        sort: params[0]?.field,
        sortDir: params[0]?.direction.toLowerCase(),
      };

      this.routeQueryUpdate(queryParamsToAdd);
    },
    setFilterParams(params, initialApply = false) {
      if (this.filter_type_report) {
        params = params.filter((el) => el.field !== "type_report")
        params.push({
          field: "type_report",
          operator: "EQUALS",
          value: this.type_reports[this.selected_type_report]
        })
      }
      storage.set(`tableFiltering_${this.$vnode.key}`, params);
      if (!initialApply) {
        this.filterParams = params;
        this.page = 1;
      }
      const queryParamsToAdd = params.map(({field, operator, value}) => {
        if (typeof value === 'object') {
          operator = `o.${operator}`;
          value = `${value.value}.${value.title}`;
        }

        return [`f_${field}`, `${operator}.${value}`];
      });

      this.routeQueryUpdate(Object.fromEntries(queryParamsToAdd), ([key]) => !key.startsWith('f_'));
    },
    entityClick(id, type, additionalData = {}) {
      if ((!id && this.canCreateModel) || (id && (this.canUpdateModel || this.canReadModel))) {
        id = id || `_temp_${+new Date()}`;
        this.$emit(
          'entityClick',
          this.entityType,
          id,
          {
            operations: this.meta.operations,
            fields: this.fieldsMeta,
          },
          additionalData,
        );
      }
    },
    updateEntity(id, data, updatedData) {
      if (!this.canUpdateModel) return;

      const draft = store.mutate.getFormDraft(this.entityType, id);
      if (draft) Object.assign(draft.data, updatedData);

      this.$apollo
        .mutate({
          mutation: UPDATE_MUTATION,
          variables: {
            id,
            type: this.entityType,
            data: preparePayload(data, this.fieldsMeta),
          },
        })
        .catch((error) => {
          this.emitError(`Ошибка сохранения; ${error}`);
        });
    },
    toggleMenu() {
      bus.$emit('toggleMenu');
    },
    updateRows(rows) {
      this.data.rows = rows;
    },
    saveConfig: debounce(function saveConfig() {
      this.$apollo
        .mutate({
          mutation: this.cachedConfig.id ? UPDATE_MUTATION : CREATE_MUTATION,
          variables: {
            type: 'Config',
            id: this.cachedConfig.id,
            data: {
              title: this.cachedConfig.title,
              config: JSON.stringify(this.cachedConfig.config),
            },
          },
        })
        .then(({data}) => {
          if (data.create) {
            this.cachedConfig.id = data.create.document.id;
          }
        })
        .catch((error) => {
          this.emitError(`Ошибка сохранения конфига; ${error}`);
        });
    }, 250),
    prepareDataForDownload(data) {
      return data.rows.map((el) => ({
        '_source': el.data,
        ...el
      }))
    },
    prepareColumnsForDownload(data) {
      return data.rows && data.rows.length ? Object.keys(data.rows[0].data).map((el) => ({
        'dataIndex': el,
        'title': el
      })) : []
    },
    async saveStoraged() {
      this.cachedConfig.config = await prepareConfig(
        this.cachedConfig.config,
        this.fieldsMeta,
        CONFIG_LAYOUT,
      );
      const storaged = {};
      const storagedKeys = ['displayFields', 'displayType', 'pageSize'];
      Object.entries(this.config).forEach(([key, value]) => {
        if (storagedKeys.includes(key)) {
          storaged[key] = value;
        }
      });
      const defaultStorage = storage.get('viewConfigData') || {};
      storage.set('viewConfigData', {
        ...defaultStorage,
        [this.$vnode.key]: storaged,
      });
    },
    updatePageSize() {
      this.page = 1;
      this.saveStoraged();
      this.updatePageQueryParams();
    },
  },
};
</script>

<style lang="scss">
.page-block {
  &__title {
    position: relative;
    flex-basis: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    height: 42px;

    h1 {
      display: inline-block;
      font-size: 2.4rem;
      font-weight: 600;
      vertical-align: sub;
      margin-bottom: 0;
      margin-right: 30px;
    }

    .menu-toggle {
      width: 30px;
      margin-right: 15px;
    }

    .ant-btn {
      position: relative;
      top: 3px;
    }
  }

  &__head-pager {
    display: flex;
    align-items: center;
  }

  & + & {
    margin-top: 50px;
  }
}

@media (min-width: $desktopBreakpoint) {
  .menu-toggle.anticon {
    display: none;
  }
}
</style>
